/**
 * preflight-http — live HTTP implementation of
 * :class:`PreflightRepository` (THE-451 PR-3c).
 *
 * Wraps ``POST /api/v1/citation-registry/preflight`` (api#580 / THE-460)
 * to partition refKeys into the ``ok`` / ``stale`` / ``broken`` buckets
 * the lawyer-facing « Vérifier tout » flow (SourcesPane) renders.
 *
 * Architecture parallels ``citation-resolver-http``: hand-rolled fetch
 * (no zod), Bearer-from-storage at call time, ``credentials: 'include'``
 * cookie fallback, duck-typed wire validation that rejects shape drift
 * loudly while tolerating missing-but-empty top-level fields.
 *
 * W7 reinforced: every status / reason / counter is BE-sourced. The
 * adapter does NO local classification — even the empty short-circuit
 * (skipping the BE on ``refKeys.length === 0``) is a transport
 * optimisation matching the BE's ``min_length=1`` 422 (we save the
 * round-trip for an unannotated doc).
 */

import {
  emptyPreflightReport,
  type PreflightBrokenEntry,
  type PreflightBrokenReason,
  type PreflightOkEntry,
  type PreflightReport,
  type PreflightRepository,
  type PreflightStaleEntry,
  type PreflightStaleReason,
  type PreflightSummary,
} from '@/domain/preflight'
import type { HttpErrorKind } from './_errors'

export interface PreflightHttpConfig {
  /** Backend base URL (no trailing slash). */
  baseUrl: string
  /** Lazy accessor for the bearer token — same lifecycle as
   *  ``citation-resolver-http`` (read at call time so logout
   *  invalidates the next request without rebuilding the adapter). */
  getAccessToken: () => string | null
  /** Fetch override for tests. */
  fetch?: typeof fetch
}

export class PreflightHttpError extends Error {
  constructor(
    public readonly kind: HttpErrorKind,
    message: string,
  ) {
    super(message)
    this.name = 'PreflightHttpError'
  }
}

const STALE_REASONS: ReadonlySet<PreflightStaleReason> = new Set([
  'document-repealed',
  'document-amended',
  'fragment-expired',
])
const BROKEN_REASONS: ReadonlySet<PreflightBrokenReason> = new Set([
  'refkey-malformed',
  'article-not-found',
  'decision-not-found',
])

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function readString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null
}

function readNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback
}

function readNonNegativeInt(value: unknown, fallback: number): number {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= 0
    ? value
    : fallback
}

function clampScore(value: number): number {
  // BE pins ``score`` to ``[0, 1]`` (Pydantic ``ge=0.0, le=1.0``). Belt-
  // and-suspenders clamp catches a future BE drift or wire transport
  // bug (e.g. NaN, ±Inf rejected upstream via Number.isFinite in
  // readNumber but not range-checked).
  if (value < 0) return 0
  if (value > 1) return 1
  return value
}

function parseOk(raw: unknown): PreflightOkEntry | null {
  if (!isPlainObject(raw)) return null
  const refKey = readString(raw.refKey)
  if (!refKey) return null
  return {
    refKey,
    verifiedAt: typeof raw.verifiedAt === 'string' ? raw.verifiedAt : null,
    score: clampScore(readNumber(raw.score, 1.0)),
  }
}

function parseStale(raw: unknown): PreflightStaleEntry | null {
  if (!isPlainObject(raw)) return null
  const refKey = readString(raw.refKey)
  if (!refKey) return null
  if (typeof raw.reason !== 'string') return null
  if (!STALE_REASONS.has(raw.reason as PreflightStaleReason)) return null
  return {
    refKey,
    lastCheck: typeof raw.lastCheck === 'string' ? raw.lastCheck : null,
    reason: raw.reason as PreflightStaleReason,
  }
}

function parseBroken(raw: unknown): PreflightBrokenEntry | null {
  if (!isPlainObject(raw)) return null
  const refKey = readString(raw.refKey)
  if (!refKey) return null
  if (typeof raw.reason !== 'string') return null
  if (!BROKEN_REASONS.has(raw.reason as PreflightBrokenReason)) return null
  return {
    refKey,
    reason: raw.reason as PreflightBrokenReason,
  }
}

function parseSummary(raw: unknown, fallback: PreflightSummary): PreflightSummary {
  if (!isPlainObject(raw)) return fallback
  return {
    total: readNonNegativeInt(raw.total, fallback.total),
    ok: readNonNegativeInt(raw.ok, fallback.ok),
    stale: readNonNegativeInt(raw.stale, fallback.stale),
    broken: readNonNegativeInt(raw.broken, fallback.broken),
  }
}

function parseReport(raw: unknown): PreflightReport {
  if (!isPlainObject(raw)) {
    throw new PreflightHttpError(
      'malformed',
      'Preflight response is not a JSON object',
    )
  }
  const okList = Array.isArray(raw.ok)
    ? raw.ok.map(parseOk).filter((e): e is PreflightOkEntry => e !== null)
    : []
  const staleList = Array.isArray(raw.stale)
    ? raw.stale
        .map(parseStale)
        .filter((e): e is PreflightStaleEntry => e !== null)
    : []
  const brokenList = Array.isArray(raw.broken)
    ? raw.broken
        .map(parseBroken)
        .filter((e): e is PreflightBrokenEntry => e !== null)
    : []
  // Summary defence-in-depth: if the BE omits or malforms the summary
  // block (unlikely — Pydantic enforces it required), derive from the
  // parsed lists. Saves us a hard fail on a recoverable wire variation.
  const derived: PreflightSummary = {
    total: okList.length + staleList.length + brokenList.length,
    ok: okList.length,
    stale: staleList.length,
    broken: brokenList.length,
  }
  return {
    ok: okList,
    stale: staleList,
    broken: brokenList,
    summary: parseSummary(raw.summary, derived),
  }
}

export function createPreflightHttp(
  config: PreflightHttpConfig,
): PreflightRepository {
  const doFetch =
    config.fetch ?? ((input, init) => globalThis.fetch(input, init))
  const url = `${config.baseUrl}/api/v1/citation-registry/preflight`

  return {
    async run(refKeys: string[]): Promise<PreflightReport> {
      if (refKeys.length === 0) return emptyPreflightReport()

      const token = config.getAccessToken()
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      }
      if (token) headers['Authorization'] = `Bearer ${token}`

      let res: Response
      try {
        res = await doFetch(url, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify({ refKeys }),
        })
      } catch (err) {
        throw new PreflightHttpError(
          'network',
          err instanceof Error ? err.message : 'Réseau injoignable',
        )
      }

      if (res.status === 401) {
        throw new PreflightHttpError(
          'auth',
          'Session expirée — reconnectez-vous',
        )
      }
      if (res.status === 429) {
        throw new PreflightHttpError(
          'rate_limited',
          'Trop de pré-vols rapprochés, réessayez dans quelques instants',
        )
      }
      if (!res.ok) {
        throw new PreflightHttpError(
          'server',
          `Préflight a échoué (HTTP ${res.status})`,
        )
      }

      let raw: unknown
      try {
        raw = await res.json()
      } catch (err) {
        throw new PreflightHttpError(
          'malformed',
          err instanceof Error
            ? `Réponse JSON invalide: ${err.message}`
            : 'Réponse JSON invalide',
        )
      }

      return parseReport(raw)
    },
  }
}
